/**
 * Pagination Utilities
 * Reusable pagination component and styles for consistent pagination across the application
 */

import React from "react";
import PropTypes from "prop-types";
import { Pagination } from "@mui/material";

/**
 * Default pagination styles
 */
export const paginationStyles = {
  "& .MuiPaginationItem-root": {
    marginRight: "1px",
    background: "white",
    color: "#2aa7e0",
    borderRadius: "0%",
    "&:hover": {
      backgroundColor: "#2e28a7ff",
      color: "white",
    },
  },
  "& .MuiPaginationItem-page.Mui-selected": {
    backgroundColor: "#2aa7e0",
    color: "white",
    "&:hover": {
      backgroundColor: "#2aa7e0",
      color: "white",
    },
  },
  "& .MuiPagination-ul": {
    // justifyContent: 'center', // Uncomment if you want center alignment
  },
  "& .MuiPaginationItem-ellipsis": {
    margin: 0,
  },
};

/**
 * Default pagination style object for inline styles
 */
export const paginationStyle = {
  marginTop: "16px",
  display: "flex",
  justifyContent: "center",
};

/**
 * Reusable Pagination Component
 * @param {Object} props - Component props
 * @param {number} props.count - Total number of pages
 * @param {number} props.page - Current page (1-based)
 * @param {Function} props.onChange - Page change handler
 * @param {Object} props.style - Custom inline styles
 * @param {Object} props.sx - Custom MUI sx styles
 * @param {boolean} props.hidePrevButton - Hide previous button
 * @param {boolean} props.hideNextButton - Hide next button
 * @param {boolean} props.showFirstButton - Show first page button
 * @param {boolean} props.showLastButton - Show last page button
 * @param {number} props.size - Size of pagination ('small', 'medium', 'large')
 * @param {string} props.color - Color theme ('primary', 'secondary', 'standard')
 * @param {boolean} props.disabled - Disable pagination
 * @param {number} props.siblingCount - Number of siblings to show
 * @param {number} props.boundaryCount - Number of boundary items to show
 */
export const ReusablePagination = ({
  count,
  page,
  onChange,
  style = paginationStyle,
  sx = paginationStyles,
  hidePrevButton = true,
  hideNextButton = true,
  showFirstButton = false,
  showLastButton = false,
  size = "medium",
  color = "primary",
  disabled = false,
  siblingCount = 1,
  boundaryCount = 1,
  ...otherProps
}) => {
  return (
    <Pagination
      count={count}
      page={page}
      onChange={onChange}
      style={style}
      sx={sx}
      hidePrevButton={hidePrevButton}
      hideNextButton={hideNextButton}
      showFirstButton={showFirstButton}
      showLastButton={showLastButton}
      size={size}
      color={color}
      disabled={disabled}
      siblingCount={siblingCount}
      boundaryCount={boundaryCount}
      {...otherProps}
    />
  );
};

ReusablePagination.propTypes = {
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  style: PropTypes.object,
  sx: PropTypes.object,
  hidePrevButton: PropTypes.bool,
  hideNextButton: PropTypes.bool,
  showFirstButton: PropTypes.bool,
  showLastButton: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  color: PropTypes.oneOf(["primary", "secondary", "standard"]),
  disabled: PropTypes.bool,
  siblingCount: PropTypes.number,
  boundaryCount: PropTypes.number,
};

/**
 * Conditional Pagination Component
 * Only shows pagination when there are more items than the specified threshold
 * @param {Object} props - Component props
 * @param {number} props.itemCount - Total number of items
 * @param {number} props.itemsPerPage - Items per page
 * @param {number} props.threshold - Minimum items to show pagination (default: 10)
 * @param {Object} props.paginationProps - Props to pass to ReusablePagination
 */
export const ConditionalPagination = ({
  itemCount,
  itemsPerPage = 10,
  threshold = 10,
  paginationProps = {},
  ...otherProps
}) => {
  const totalPages = Math.ceil(itemCount / itemsPerPage);

  if (itemCount <= threshold) {
    return null;
  }

  return <ReusablePagination count={totalPages} {...paginationProps} {...otherProps} />;
};

ConditionalPagination.propTypes = {
  itemCount: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number,
  threshold: PropTypes.number,
  paginationProps: PropTypes.object,
};

/**
 * Advanced Pagination Component with additional features
 * @param {Object} props - Component props
 * @param {Array} props.data - Data array to paginate
 * @param {number} props.itemsPerPage - Items per page
 * @param {number} props.currentPage - Current page
 * @param {Function} props.onPageChange - Page change handler
 * @param {Function} props.renderItem - Function to render each item
 * @param {Object} props.paginationConfig - Pagination configuration
 */
export const AdvancedPagination = ({
  data = [],
  itemsPerPage = 10,
  currentPage = 1,
  onPageChange,
  renderItem,
  paginationConfig = {},
  ...otherProps
}) => {
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  return (
    <div>
      {/* Render current page data */}
      {currentData.map((item, index) => (renderItem ? renderItem(item, startIndex + index) : null))}

      {/* Render pagination */}
      {data.length > itemsPerPage && (
        <ReusablePagination
          count={totalPages}
          page={currentPage}
          onChange={(event, page) => onPageChange(page)}
          {...paginationConfig}
          {...otherProps}
        />
      )}
    </div>
  );
};

AdvancedPagination.propTypes = {
  data: PropTypes.array,
  itemsPerPage: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  renderItem: PropTypes.func,
  paginationConfig: PropTypes.object,
};

/**
 * Pagination hook for managing pagination state
 * @param {number} initialPage - Initial page number
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Pagination state and handlers
 */
export const usePagination = (initialPage = 1, itemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = React.useState(initialPage);
  const [pageSize, setPageSize] = React.useState(itemsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
    setCurrentPage,
    setPageSize,
  };
};

/**
 * Pagination constants
 */
export const PAGINATION_CONSTANTS = {
  DEFAULT_ITEMS_PER_PAGE: 10,
  DEFAULT_THRESHOLD: 10,
  DEFAULT_SIBLING_COUNT: 1,
  DEFAULT_BOUNDARY_COUNT: 1,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

export default {
  ReusablePagination,
  ConditionalPagination,
  AdvancedPagination,
  usePagination,
  paginationStyles,
  paginationStyle,
  PAGINATION_CONSTANTS,
};
